import { Box } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import CheckIcon from '@mui/icons-material/Check'

export type WizardStep = {
  label: string
  /** What a finished step holds ("2 · 1:05") — shown as the tab's tooltip. */
  summary?: string
}

type WizardStepperProps = {
  steps: WizardStep[]
  current: number
  /** Jump to a step; a step that cannot be opened yet is not clickable. */
  onSelect: (index: number) => void
  canSelect: (index: number) => boolean
}

/** The wizard's steps as pills joined by a hairline: the open one filled
 *  dark, done ones with a check, the rest grey. */
export default function WizardStepper({ steps, current, onSelect, canSelect }: WizardStepperProps) {
  const c = useTheme().affexy.colors

  return (
    <Box component="nav" role="tablist" sx={{ display: 'flex', alignItems: 'center', overflowX: 'auto', minWidth: 0, scrollbarWidth: 'none' }}>
      {steps.map((step, i) => {
        const done = i < current
        const active = i === current
        const enabled = !active && canSelect(i)
        return (
          <Box key={step.label} sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            {i > 0 && <Box aria-hidden sx={{ width: { xs: 12, md: 24 }, height: '1px', bgcolor: done || active ? c.ink32 : c.ink08, mx: 0.5 }} />}
            <Box
              component="button"
              type="button"
              role="tab"
              aria-selected={active}
              disabled={!active && !enabled}
              title={done ? step.summary || undefined : undefined}
              onClick={() => enabled && onSelect(i)}
              sx={{
                font: 'inherit',
                border: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 0.875,
                height: 34,
                pl: 0.625,
                pr: 1.5,
                borderRadius: 99,
                fontSize: 14,
                fontWeight: active ? 700 : 500,
                bgcolor: active ? c.ink : 'transparent',
                color: active ? '#fff' : done ? c.ink : c.ink48,
                cursor: enabled ? 'pointer' : 'default',
                whiteSpace: 'nowrap',
                transition: 'background-color .18s ease, color .18s ease',
                '&:hover': enabled ? { bgcolor: 'rgba(27,28,56,.06)' } : undefined,
                '&:active': enabled ? { transform: 'scale(.97)' } : undefined,
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 11.5,
                  fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums',
                  bgcolor: active ? c.coral : done ? 'rgba(31,138,91,.12)' : '#EEEEF3',
                  color: active ? '#fff' : done ? '#1F8A5B' : c.ink48,
                }}
              >
                {done ? <CheckIcon sx={{ fontSize: 14 }} /> : i + 1}
              </Box>
              {step.label}
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}
